import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  verifyPasswordResetCode,
  confirmPasswordReset,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";

import { auth } from "./firebase-config.js";

// Persistence is chosen per flow so the authenticated state is only retained
// for users who work for a business (Owner / Administrator / Employee).
//
//  - Business staff sign-in/sign-up uses browserLocalPersistence: the session
//    survives tab close and browser restart, so the dashboard stays logged in.
//  - End-customer enrollment uses browserSessionPersistence: the session is
//    scoped to the current tab only. It lives long enough for wallet-pass
//    generation (and a retry) but is never written to localStorage, so a
//    customer is never left in a persisted authenticated state.
export const useBusinessSessionPersistence = () =>
  setPersistence(auth, browserLocalPersistence);

export const useCustomerSessionPersistence = () =>
  setPersistence(auth, browserSessionPersistence);

const AUTH_ERROR_MESSAGES = {
  "auth/invalid-email": "Please enter a valid email address.",
  "auth/user-disabled": "This account has been disabled. Please contact support.",
  "auth/user-not-found": "No account found with this email address.",
  "auth/network-request-failed": "Network error. Please check your connection and try again.",
  "auth/too-many-requests": "Too many attempts. Please wait a moment and try again.",
  "auth/weak-password": "Password is too weak. Please choose a stronger password.",
  "auth/missing-password": "Please enter a password.",
  "auth/operation-not-allowed": "This sign-in method is not enabled. Please contact support.",
  "auth/invalid-action-code": "This reset link is invalid or has already been used.",
  "auth/expired-action-code": "This reset link has expired. Please request a new one.",
  "auth/email-already-in-use": "An account with this email address already exists.",
  "auth/wrong-password": "Incorrect password. Please try again.",
  "auth/invalid-credential": "Email or password is incorrect. Please try again.",
};

export const mapAuthError = (error) => {
  const message = AUTH_ERROR_MESSAGES[error.code];
  if (message) {
    const mapped = new Error(message);
    mapped.code = error.code;
    return mapped;
  }
  return error;
};

export const createNewCustomer = async (emailAddress, password) => {
  return createUserWithEmailAndPassword(auth, emailAddress, password);
};

export const signUserInWithEmailAndPassword = async (emailAddress, password) => {
  return signInWithEmailAndPassword(auth, emailAddress, password);
};

export const isCustomerAuthenticated = () => {
  return auth.currentUser != null;
};

export const sendResetEmail = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw mapAuthError(error);
  }
}

export async function verifyCode(oobCode) {
  try {
    const email = await verifyPasswordResetCode(auth, oobCode);
    return { success: true, email };
  } catch (error) {
    return { success: false, error };
  }
}

export async function resetPassword(oobCode, newPassword) {
  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
}